import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Container, Card, Table, Form, Button, Modal, Nav, Tab, Spinner, Badge } from "react-bootstrap";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";
import { API_BASE } from "../../services/apirest";

const TABS_CONFIG = [
    { key: "solicitudes", label: "Solicitudes", table: "solicitudes_usuarios", pk: "SolicitudId" },
    { key: "plantilla", label: "Plantilla", table: "usuarios", pk: "UsuarioId" },
    { key: "roles", label: "Roles", table: "roles", pk: "id" },
    { key: "distribuidores", label: "Distribuidores", table: "distribuidores", pk: "id_distribuidor" },
    { key: "gerentes", label: "Gerentes", table: "gerentes", pk: "id_gerente" },
    { key: "regiones", label: "Regiones", table: "region", pk: "id" },
    { key: "bodegas", label: "Bodegas", table: "bodegas", pk: "id" },
    { key: "uso_clave", label: "Uso de Clave", table: "uso_clave", pk: "id" },
    { key: "paquete_izzi", label: "Paquetes IZZI", table: "paquete_izzi", pk: "id" },
    { key: "black_list", label: "Blacklist", table: "black_list", pk: "id" },
];

export default function AdminDatabase() {
  const [activeTabKey, setActiveTabKey] = useState("solicitudes");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isNewRecord, setIsNewRecord] = useState(false);
  const [showPasteModal, setShowPasteModal] = useState(false);
  const [pasteData, setPasteData] = useState("");

  const currentConfig = useMemo(() => TABS_CONFIG.find(t => t.key === activeTabKey), [activeTabKey]);

  const headers = useMemo(() => ({
    "Content-Type": "application/json",
    Authorization: `Bearer ${localStorage.getItem("token")}`,
  }), []);

  const fetchData = useCallback(async () => {
    if (!currentConfig) return;
    setLoading(true);
    setSelectedIds([]);
    try {
      const res = await fetch(`${API_BASE}/admin_db.php?table=${currentConfig.table}&pk=${currentConfig.pk}`, { headers });
      const json = await res.json();
      if (json.status === "success" || json.ok) {
        let list = json.result[currentConfig.table] || [];
        // Ordenar por ID descendente
        list.sort((a,b) => {
            const v1 = a[currentConfig.pk];
            const v2 = b[currentConfig.pk];
            return (typeof v1 === 'number' && typeof v2 === 'number') ? v2 - v1 : 0;
        });
        setData(list);
      } else {
        setData([]);
      }
    } catch (err) {
      console.error(err);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [currentConfig, headers]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filteredData = useMemo(() => {
    if (!searchTerm) return data;
    const low = searchTerm.toLowerCase();
    return data.filter(item => 
      Object.values(item).some(v => String(v || "").toLowerCase().includes(low))
    );
  }, [data, searchTerm]);

  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === filteredData.length && filteredData.length > 0) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredData.map(i => i[currentConfig.pk]));
    }
  };

  const handleNuevo = () => {
    if (!data.length && !currentConfig) return;
    // Crear objeto con todas las keys de la tabla pero vacías
    const emptyRow = {};
    if (data.length > 0) {
        Object.keys(data[0]).forEach(k => emptyRow[k] = "");
    } else {
        // Si no hay data, al menos la PK
        emptyRow[currentConfig.pk] = "";
    }
    setSelectedItem(emptyRow);
    setIsNewRecord(true);
    setShowEditModal(true);
  };

  const handleEliminarSingle = async (id) => {
    const result = await Swal.fire({
      title: "¿Eliminar físicamente?",
      text: `ID: ${id}. Esta acción no se puede deshacer.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: '#d33'
    });

    if (result.isConfirmed) {
      try {
        const res = await fetch(`${API_BASE}/admin_db.php?table=${currentConfig.table}&pk=${currentConfig.pk}`, {
          method: "DELETE",
          headers,
          body: JSON.stringify({ id })
        });
        const json = await res.json();
        if (json.status === "success" || json.ok) {
          Swal.fire("Eliminado", "", "success");
          fetchData();
        }
      } catch (err) {
        Swal.fire("Error", "Fallo de conexión", "error");
      }
    }
  };

  const handleBulkDelete = async () => {
    const result = await Swal.fire({
      title: `¿Eliminar ${selectedIds.length} registros?`,
      text: "Eliminación masiva física en la tabla " + currentConfig.table,
      icon: "error",
      showCancelButton: true,
      confirmButtonColor: '#d33',
      confirmButtonText: "SÍ, ELIMINAR TODO"
    });

    if (result.isConfirmed) {
      setLoading(true);
      try {
        const res = await fetch(`${API_BASE}/admin_db.php?table=${currentConfig.table}&pk=${currentConfig.pk}&action=bulk_delete`, {
          method: "POST",
          headers,
          body: JSON.stringify({ ids: selectedIds })
        });
        const json = await res.json();
        if (json.status === "success" || json.ok) {
          Swal.fire("Éxito", "Registros eliminados", "success");
          fetchData();
        }
      } catch (err) {
        Swal.fire("Error", "Error en borrado masivo", "error");
      } finally {
        setLoading(false);
      }
    }
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    try {
      const method = isNewRecord ? "POST" : "PUT";
      const action = isNewRecord ? "&action=bulk_insert" : "";
      const body = isNewRecord ? [selectedItem] : selectedItem; // POST espera un array para batch
      
      const res = await fetch(`${API_BASE}/admin_db.php?table=${currentConfig.table}&pk=${currentConfig.pk}${action}`, {
        method: method,
        headers,
        body: JSON.stringify(body)
      });
      const json = await res.json();
      if (json.status === "success" || json.ok) {
        Swal.fire("Guardado", "", "success");
        setShowEditModal(false);
        fetchData();
      } else {
          Swal.fire("Error", json.result?.error_msg || "Error al guardar", "error");
      }
    } catch (err) {
      Swal.fire("Error", "Error de red", "error");
    }
  };

  const handlePasteProcess = async () => {
    if (!pasteData.trim()) return;
    const lines = pasteData.split("\n").filter(l => l.trim() !== "");
    if (lines.length < 2) {
       Swal.fire("Error", "Copia encabezados y al menos una fila", "error");
       return;
    }
    
    const headersRaw = lines[0].split("\t").map(h => h.trim());
    const rows = [];
    for (let i = 1; i < lines.length; i++) {
        const cols = lines[i].split("\t");
        if (cols.length === headersRaw.length) {
            const obj = {};
            headersRaw.forEach((h, idx) => {
                const val = cols[idx]?.trim();
                obj[h] = (val === "" || val === "NULL" || val === undefined) ? null : val;
            });
            rows.push(obj);
        }
    }

    try {
        const res = await fetch(`${API_BASE}/admin_db.php?table=${currentConfig.table}&action=bulk_insert`, {
            method: "POST",
            headers,
            body: JSON.stringify(rows)
        });
        const json = await res.json();
        if (json.status === "success" || json.ok) {
            Swal.fire("Éxito", `Insertados: ${json.result?.result?.inserted || json.result?.inserted}`, "success");
            setShowPasteModal(false);
            setPasteData("");
            fetchData();
        }
    } catch (err) {
        Swal.fire("Error", "Error en lote", "error");
    }
  };

  const handleExportar = () => {
    const ws = XLSX.utils.json_to_sheet(filteredData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, currentConfig.label);
    XLSX.writeFile(wb, `${currentConfig.label}_Maestro.xlsx`);
  };

  return (
    <Container className="my-5 pt-5 mt-5">
      <Card className="shadow-sm border-0">
        <Card.Header className="bg-dark text-white p-3 d-flex justify-content-between align-items-center">
            <h4 className="mb-0">DB MAESTRO - {currentConfig?.label}</h4>
            <div className="d-flex gap-2">
                {selectedIds.length > 0 && (
                    <Button variant="danger" size="sm" onClick={handleBulkDelete}>
                        Eliminar Seleccionados ({selectedIds.length})
                    </Button>
                )}
                <Badge bg="danger" className="p-2 d-flex align-items-center">MAESTRO</Badge>
            </div>
        </Card.Header>
        <Card.Body>
            <Nav variant="pills" className="mb-4 flex-wrap" activeKey={activeTabKey} onSelect={(k) => setActiveTabKey(k)}>
                {TABS_CONFIG.map(t => (
                    <Nav.Item key={t.key} className="me-1 mb-1">
                        <Nav.Link eventKey={t.key} size="sm" style={{fontSize:'0.72rem'}} className="py-1">{t.label}</Nav.Link>
                    </Nav.Item>
                ))}
            </Nav>

            <div className="d-flex justify-content-between mb-3 flex-wrap gap-2 align-items-center">
                <Form.Control size="sm" type="text" placeholder="Filtrar..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} style={{ maxWidth: '250px' }} />
                <div className="d-flex gap-2 flex-wrap">
                    <Button size="sm" variant="primary" onClick={handleNuevo}>+ Nuevo Registro</Button>
                    <Button size="sm" variant="info" className="text-white" onClick={() => setShowPasteModal(true)}>Excel Paste</Button>
                    <Button size="sm" variant="outline-success" onClick={handleExportar}>Exportar</Button>
                    <Button size="sm" variant="outline-secondary" onClick={fetchData}>Refrescar</Button>
                </div>
            </div>

            {loading ? (
              <div className="text-center py-5"><Spinner animation="border" /></div>
            ) : (
              <div className="table-responsive" style={{ maxHeight: '550px', border: '1px solid #eee' }}>
                <Table striped bordered hover size="sm" className="text-nowrap mb-0" style={{ fontSize: '0.74rem' }}>
                  <thead className="bg-light sticky-top" style={{ zIndex: 5 }}>
                    <tr>
                      <th className="text-center" style={{ width: '40px' }}><Form.Check checked={selectedIds.length > 0 && selectedIds.length === filteredData.length} onChange={toggleSelectAll} /></th>
                      {data.length > 0 && Object.keys(data[0]).map(k => <th key={k}>{k}</th>)}
                      <th className="text-center">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredData.map((item, idx) => {
                      const pkValue = item[currentConfig.pk];
                      return (
                        <tr key={idx}>
                          <td className="text-center"><Form.Check checked={selectedIds.includes(pkValue)} onChange={() => toggleSelect(pkValue)} /></td>
                          {Object.keys(item).map(k => <td key={k}>{String(item[k] || "")}</td>)}
                          <td className="text-center">
                            <div className="d-flex gap-1 justify-content-center">
                              <Button size="sm" variant="outline-warning" className="py-0 px-2" style={{lineHeight:1.2}} onClick={() => { setSelectedItem({...item}); setIsNewRecord(false); setShowEditModal(true); }}>Edit</Button>
                              <Button size="sm" variant="outline-danger" className="py-0 px-2" style={{lineHeight:1.2}} onClick={() => handleEliminarSingle(pkValue)}>X</Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                    {filteredData.length === 0 && (
                        <tr><td colSpan={100} className="text-center p-4 text-muted">No se encontraron registros en {currentConfig.table}</td></tr>
                    )}
                  </tbody>
                </Table>
              </div>
            )}
        </Card.Body>
      </Card>

      {/* MODAL EDITAR/NUEVO GENERICO */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg">
        <Modal.Header closeButton><Modal.Title>{isNewRecord ? 'Crear Nuevo' : 'Editar'} en {currentConfig.label}</Modal.Title></Modal.Header>
        <Modal.Body>
            <Form onSubmit={handleSaveEdit}>
                <div className="row">
                    {selectedItem && Object.keys(selectedItem).map(k => (
                        <Form.Group className="mb-2 col-md-4" key={k}>
                            <Form.Label style={{fontSize:'0.68rem'}} className="fw-bold mb-0">{k}</Form.Label>
                            <Form.Control size="sm" 
                                type="text" 
                                disabled={!isNewRecord && k === currentConfig.pk} 
                                value={selectedItem[k] || ""} 
                                onChange={(e) => setSelectedItem({...selectedItem, [k]: e.target.value})} 
                            />
                        </Form.Group>
                    ))}
                </div>
                <div className="text-end mt-3"><Button size="sm" variant="primary" type="submit">{isNewRecord ? 'Insertar' : 'Guardar Cambios'}</Button></div>
            </Form>
        </Modal.Body>
      </Modal>

      {/* MODAL PEGAR EXCEL GENERICO */}
      <Modal show={showPasteModal} onHide={() => setShowPasteModal(false)} size="xl">
        <Modal.Header closeButton><Modal.Title>Excel Paste: {currentConfig.label}</Modal.Title></Modal.Header>
        <Modal.Body>
            <p className="small text-muted mb-2">Pega filas con encabezados incluidos. Nombres deben coincidir con columnas DB.</p>
            <Form.Control as="textarea" rows={12} placeholder="Pega desde Excel aquí..." value={pasteData} onChange={(e) => setPasteData(e.target.value)} className="font-monospace small" />
        </Modal.Body>
        <Modal.Footer><Button size="sm" variant="success" onClick={handlePasteProcess}>Insertar Datos Masivamente</Button></Modal.Footer>
      </Modal>
    </Container>
  );
}
